import { createConfig, http } from 'wagmi';
import { injected, walletConnect, coinbaseWallet } from 'wagmi/connectors';
import {
  mainnet,
  sepolia,
  base,
  baseSepolia,
  arbitrum,
  arbitrumSepolia,
  bsc,
  bscTestnet,
  polygon,
  avalanche,
  optimism,
  sonic,
  hyperEvm,
  lightlinkPhoenix,
  kaia,
  redbellyMainnet,
} from '@wagmi/core/chains';
import type { Chain } from '@wagmi/core/chains';
import {
  VITE_BRIDGE_NETWORK_TYPE,
  VITE_BRIDGE_WALLETCONNECT_PROJECT_ID,
  VITE_APPKIT_PROJECT_ID,
  VITE_ETHEREUM_RPC_URL,
  VITE_ARBITRUM_RPC_URL,
  VITE_BASE_RPC_URL,
  VITE_BSC_RPC_URL,
} from '@/configs/bridge/bridgeEnv';

const isMainnet = VITE_BRIDGE_NETWORK_TYPE === 'mainnet';

const projectId = VITE_APPKIT_PROJECT_ID || VITE_BRIDGE_WALLETCONNECT_PROJECT_ID || '';

const metadata = {
  name: 'MoneyMarket Bridge',
  description: 'Bridge between Ethereum and Aleo',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://moneymarket.io',
  icons: [typeof window !== 'undefined' ? `${window.location.origin}/favicon.ico` : '']
};

// App-wide EVM chain set. Bridge signs on a subset (Ethereum / Base / Arbitrum / BSC),
// swap needs read access on the full list. Widening the config lets one wagmi instance
// serve both — no second provider, no cross-provider sync.
const mainnetChains = [
  mainnet,
  base,
  arbitrum,
  bsc,
  polygon,
  avalanche,
  optimism,
  sonic,
  hyperEvm,
  lightlinkPhoenix,
  kaia,
  redbellyMainnet,
] as const;
const testnetChains = [sepolia, baseSepolia, arbitrumSepolia, bscTestnet] as const;

export const networks: readonly [Chain, ...Chain[]] = (isMainnet ? mainnetChains : testnetChains) as unknown as readonly [Chain, ...Chain[]];

// Prefer env-configured RPCs over viem's chain defaults — the built-in public
// endpoints (cloudflare-eth.com etc.) rate-limit aggressively, so parallel
// balance queries silently drop tokens. Fall back to http() defaults for
// chains the user hasn't configured.
const RPC_OVERRIDES: Record<number, string | undefined> = {
  1: VITE_ETHEREUM_RPC_URL,
  8453: VITE_BASE_RPC_URL,
  42161: VITE_ARBITRUM_RPC_URL,
  56: VITE_BSC_RPC_URL,
};

const transports = Object.fromEntries(
  networks.map((c) => {
    const override = RPC_OVERRIDES[c.id];
    return [c.id, override ? http(override) : http()];
  })
) as Record<number, ReturnType<typeof http>>;

export const wagmiConfig = createConfig({
  chains: networks as unknown as readonly [Chain, ...Chain[]],
  connectors: [
    injected({ shimDisconnect: true }),
    ...(projectId
      ? [walletConnect({ projectId, showQrModal: true, metadata })]
      : []),
    coinbaseWallet({ appName: metadata.name, appLogoUrl: metadata.icons[0] })
  ],
  transports,
  ssr: false
});
